import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}



(function() {
  const UPSELL_MAP = {
    '56668539748684': '56668560458060',
    '56668560458060': '56668539748684',
    '56655717040460': '56655725494604',
    '56655725494604': '56655717040460',
  };

  const SECTIONS_TO_UPDATE = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items'];

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function getVariant(variantId) {
    const res = await fetch(`/variants/${variantId}.js`);
    return res.json();
  }

  async function addToCart(variantId, btn) {
    btn.disabled = true;
    btn.textContent = 'Hinzufügen...';

    try {
      const res = await fetch(
        (window.Shopify?.routes?.root || '/') + 'cart/add.js',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: SECTIONS_TO_UPDATE.join(','),
            sections_url: window.location.pathname
          })
        }
      );

      const parsedResponse = await res.json();

      // Update sections
      if (parsedResponse.sections) {
        Object.entries(parsedResponse.sections).forEach(([sectionId, html]) => {
          const targets = document.querySelectorAll(`#${sectionId}, [data-section-id="${sectionId}"]`);
          targets.forEach(el => {
            const temp = document.createElement('div');
            temp.innerHTML = html;
            const inner = temp.querySelector(`#${sectionId}, [data-section-id="${sectionId}"]`);
            el.innerHTML = inner ? inner.innerHTML : html;
          });
        });
      }

      // Trigger events
      document.dispatchEvent(new CustomEvent('cart-add', {
        bubbles: true,
        detail: { response: parsedResponse }
      }));

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: parsedResponse,
          sourceId: 'upsell-section',
          data: {
            itemCount: parsedResponse.item_count,
            sections: parsedResponse.sections
          }
        }
      }));

      btn.classList.add('cart-upsell__btn--success');
      btn.textContent = '✓ Hinzugefügt';

      setTimeout(() => renderUpsell(), 500);

    } catch (e) {
      console.error('[cart-upsell] error:', e);
      btn.textContent = 'Fehler – erneut versuchen';
      btn.disabled = false;
    }
  }

  async function renderUpsell() {
    const wrapper = document.getElementById('cart-upsell-wrapper');
    if (!wrapper) return;

    const cart = await getCart();

    if (!cart.items || cart.items.length === 0) {
      wrapper.innerHTML = '';
      return;
    }

    let upsellVariantId = null;
    let sourceItem = null;

    for (const item of cart.items) {
      const vid = String(item.variant_id);
      if (UPSELL_MAP[vid]) {
        upsellVariantId = UPSELL_MAP[vid];
        sourceItem = item;
        break;
      }
    }

    if (!upsellVariantId) {
      wrapper.innerHTML = '';
      return;
    }

    const alreadyInCart = cart.items.some(
      i => String(i.variant_id) === String(upsellVariantId)
    );

    if (alreadyInCart) {
      wrapper.innerHTML = '';
      return;
    }

    const variant = await getVariant(upsellVariantId);
    if (!variant || variant.available === false) {
      wrapper.innerHTML = '';
      return;
    }

    // ✅ FIXED IMAGE LOGIC (multi fallback)
    const image =
      sourceItem?.featured_image?.url ||
      sourceItem?.image ||
      variant?.featured_image?.src ||
      variant?.featured_image ||
      'https://via.placeholder.com/150';

    // ✅ FULL TITLE (no cut)
    const productTitle = variant.name;

    const price = (variant.price / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });

    wrapper.innerHTML = `
      <div class="cart-upsell">
        <p class="cart-upsell__title">Wird oft zusammen gekauft</p>
        <div class="cart-upsell__card">
          <div class="cart-upsell__image-wrap">
            <img src="${image}" alt="${productTitle}" class="cart-upsell__image">
          </div>
          <div class="cart-upsell__body">
            <p class="cart-upsell__product-title">${productTitle}</p>
            <p class="cart-upsell__price">${price}</p>
            <button
              class="cart-upsell__btn"
              data-variant-id="${upsellVariantId}"
              id="upsell-add-btn"
            >
              In den Warenkorb
            </button>
          </div>
        </div>
      </div>
    `;

    document
      .getElementById('upsell-add-btn')
      .addEventListener('click', function() {
        addToCart(upsellVariantId, this);
      });
  }

  function boot() {
    renderUpsell();

    document.addEventListener('dialog-open', () =>
      setTimeout(renderUpsell, 150)
    );

    document.addEventListener('cart-add', () =>
      setTimeout(renderUpsell, 400)
    );

    document.addEventListener('cart:update', () =>
      setTimeout(renderUpsell, 300)
    );

    document.addEventListener('cart:refresh', () =>
      setTimeout(renderUpsell, 300)
    );

    document.addEventListener('shopify:section:load', () =>
      setTimeout(renderUpsell, 200)
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();